import cn from "classnames";
import React, { useEffect, useState } from "react";
import {
  Bar,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ReactComponent as ZoomInIcon } from "@/shared/icons/zoom-in.svg";
import { ReactComponent as ZoomOutIcon } from "@/shared/icons/zoom-out.svg";
import { ChartRiskLiquidity } from "@/shared/types/chart-risk";
import { formatNum } from "@/shared/utils/format";

import { Token } from "../../constants";
import { LINE_CHART_MARGIN_PARAMS_PX } from "../../constants";
import { ChartWrapper } from "../../styled/chart-wrapper";
import { Tooltip as TooltipStyled, TooltipRow } from "../../styled/tooltip";
import { getBrandColorByToken } from "../../utils/get-brand-color-by-token";
import { getIconByToken } from "../../utils/get-icon-by-token";
import { numberToPercent } from "../../utils/number-to-percent";
import { createGetBarColor } from "./create-get-bar-color";
import * as S from "./styled";

const ZOOM_DATA_SIZE = 40;

const getZoomedData = (
  data: ChartRiskLiquidity["points"],
  zoomLevel: number,
) => {
  const rate = ZOOM_DATA_SIZE / 2;
  if (zoomLevel < 1) {
    return data;
  }
  return data.slice(zoomLevel * rate, data.length - zoomLevel * rate);
};

const getLiquidityTooltip = (
  value?: ChartRiskLiquidity["points"][number],
  token0?: string,
  token1?: string,
) => {
  if (!value) return "";
  const { token0Amount, token1Amount, liquidityInToken0 } = value;
  if (token0Amount && token1Amount) {
    return `${formatNum(liquidityInToken0)} ${token0}`;
  }
  if (token0Amount) {
    return `${formatNum(token0Amount)} ${token0}`;
  }
  if (token1Amount) {
    return `${formatNum(token1Amount)} ${token1}`;
  }
};

interface Props {
  data?: ChartRiskLiquidity;
}

export const LiquidityChart: React.FC<Props> = React.memo(({ data }) => {
  const [zoomLevel, setZoomLevel] = useState(0);

  useEffect(() => {
    setZoomLevel(0);
  }, [data]);

  const chartData = [...(data?.points || [])].reverse();

  const maxZoomLevel = chartData.length / ZOOM_DATA_SIZE - 1;
  const zoomedData = getZoomedData(chartData, zoomLevel);
  const currentTickIndex = chartData?.findIndex((el) => el.isCurrentTick) || 0;
  const currentTick = chartData[currentTickIndex];
  const getBarColor = createGetBarColor(currentTick?.priceInToken0 || 0);

  const token1 = data?.token1 as Token | undefined;
  const token0 = data?.token0 as Token | undefined;
  return (
    <>
      <S.Wrapper>
        <ChartWrapper>
          <ResponsiveContainer>
            <ComposedChart
              data={zoomedData}
              margin={LINE_CHART_MARGIN_PARAMS_PX}
            >
              <XAxis tickLine={false} tickFormatter={() => ""} />
              <YAxis
                tickLine={false}
                tickFormatter={() => ""}
                yAxisId="left"
                orientation="left"
                stroke="#518ce9"
                label={{
                  value: "Liquidity",
                  angle: -90,
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tickFormatter={(value) => numberToPercent(value / 100)}
                stroke="#62bc51"
                label={{ value: "IV", angle: 90, position: "insideRight" }}
              />
              <Tooltip
                content={(value) => {
                  return (
                    <TooltipStyled>
                      <TooltipRow color="#62bc51">
                        at tick volatility:{" "}
                        {numberToPercent(value?.payload?.[0]?.payload.iv / 100)}
                      </TooltipRow>
                      <TooltipRow color="#518ce9">
                        value locked:{" "}
                        {getLiquidityTooltip(
                          value?.payload?.[0]?.payload,
                          data?.token0,
                          data?.token1,
                        )}
                      </TooltipRow>
                      <TooltipRow>
                        {data?.token0} price:{" "}
                        {formatNum(value?.payload?.[0]?.payload.priceInToken1)}{" "}
                        {data?.token1}
                      </TooltipRow>
                      <TooltipRow>
                        {data?.token1} price:{" "}
                        {formatNum(value?.payload?.[0]?.payload.priceInToken0)}{" "}
                        {data?.token0}
                      </TooltipRow>
                    </TooltipStyled>
                  );
                }}
              />
              <Bar yAxisId="left" dataKey="liquidityInToken0">
                {zoomedData.map((entry) => {
                  return (
                    <Cell
                      fill={
                        entry.isCurrentTick
                          ? "#ff007b"
                          : getBarColor(entry.priceInToken0)
                      }
                      key={entry.tick}
                    />
                  );
                })}
              </Bar>
              <Line
                type="monotone"
                dataKey="iv"
                yAxisId="right"
                stroke="#fff"
                strokeWidth={3}
                dot={<span />}
              />
              <Line
                type="monotone"
                dataKey="iv"
                yAxisId="right"
                stroke="#4faf4e"
                dot={<span />}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartWrapper>
        <S.ZoomSection>
          <S.ZoomButton
            onClick={() => setZoomLevel((el) => el - 1)}
            className={cn({ disabled: zoomLevel < 1 })}
          >
            <ZoomOutIcon />
          </S.ZoomButton>
          <S.ZoomButton
            onClick={() => setZoomLevel((el) => el + 1)}
            className={cn({ disabled: zoomLevel > maxZoomLevel })}
          >
            <ZoomInIcon />
          </S.ZoomButton>
        </S.ZoomSection>
      </S.Wrapper>

      {data && (
        <S.CurrentlyInfoSection>
          <S.TokenCost>
            {token0 && (
              <S.TokenIconWrapper $background={getBrandColorByToken(token0)}>
                {getIconByToken(token0)}
              </S.TokenIconWrapper>
            )}
            <div>
              {formatNum(currentTick?.priceInToken1)} {token1}
              <S.TokenCostDescription>for 1 {token0}</S.TokenCostDescription>
            </div>
          </S.TokenCost>
          <S.TokenCost>
            {token1 && (
              <S.TokenIconWrapper $background={getBrandColorByToken(token1)}>
                {getIconByToken(token1)}
              </S.TokenIconWrapper>
            )}
            <div>
              {formatNum(currentTick?.priceInToken0)} {token0}
              <S.TokenCostDescription>for 1 {token1}</S.TokenCostDescription>
            </div>
          </S.TokenCost>
        </S.CurrentlyInfoSection>
      )}
    </>
  );
});
